import clsx from 'clsx'
import React, { forwardRef, useEffect, useRef } from 'react'
import { Input as UiInput } from '@/components/ui/input'
import { Label } from './Label'
import { cn } from '@/lib/utils'
import { Field, FieldError } from 'react-hook-form'

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: FieldError
  className?: string
  required?: boolean
  numberOnly?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { label, required, className, error, numberOnly, onChange, ...props },
    forwardedRef
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)
    useEffect(() => {
      if (forwardedRef) {
        if (typeof forwardedRef === 'function') {
          forwardedRef(inputRef.current)
        } else if (typeof forwardedRef === 'object') {
          forwardedRef.current = inputRef.current
        }
      }
    }, [forwardedRef])

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.setCustomValidity(error?.message || '')
      }
    }, [error])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (numberOnly) {
        const numericValue = e.target.value.replace(/[^0-9]/g, '')
        e.target.value = numericValue
        if (onChange) {
          const numberValue = parseFloat(numericValue)
          const syntheticEvent = {
            ...e,
            target: {
              ...e.target,
              value: isNaN(numberValue) ? '' : numberValue.toString(),
            },
          }
          onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>)
        }
      } else if (onChange) {
        onChange(e)
      }
    }

    return (
      <div className={cn('space-y-0', className)}>
        {label && (
          <Label label={`${label}${required ? '*' : ''}`} error={!!error} />
        )}
        <UiInput
          ref={inputRef}
          {...props}
          onChange={handleChange}
          error={!!error}
        />
        {error && <p className="text-sm text-red-600">{error.message}</p>}
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input
